import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BasicLayout from "components/Layouts/BasicLayout";
import stripeService from "services/stripe";
import { StripeCharge } from "types/stripe";
import { formatCurrency, formatDate } from "utils/commonFunctions";
import LoadingOverlay from "components/LoadingOverlay";
import StripeRefundButton from "components/StripeRefundButton";
import { useGlobalContext } from "context/GlobalContext";

const ChargeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [charge, setCharge] = useState<StripeCharge | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useGlobalContext();

  const fetchCharge = useCallback(async () => {
    try {
      setLoading(true);
      const data = await stripeService.fetchCharge(id as string);
      setCharge(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching charge:", err);
      setError("Failed to load charge details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchCharge();
    }
  }, [id, fetchCharge]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "succeeded":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return formatDate(new Date(timestamp * 1000));
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error || !charge) {
    return (
      <BasicLayout>
        <div className="p-6">
          <h6 className="text-red-600 text-xl font-semibold">
            {error || "Charge not found"}
          </h6>
          <button
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={() => navigate(-1)}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        </div>
      </BasicLayout>
    );
  }

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    showToast("Email copied to clipboard");
  };

  return (
    <BasicLayout>
      <div className="p-6">
        <button
          className="mb-6 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          onClick={() => navigate("/charges")}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Charges
        </button>

        <div className="grid gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-start items-center gap-2 mb-2">
                    <h5 className="text-2xl font-semibold">Charge Details</h5>
                    <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(charge.status)}`}>
                      {charge.refunded ? "Refunded" : charge.status.charAt(0).toUpperCase() + charge.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-lg mb-2">ID: {charge.id}</p>

                </div>
                <div className="sm:text-right">
                  <h4 className="text-3xl font-bold mb-2">
                    {formatCurrency(charge.amount, charge.currency)}
                  </h4>
                  <p className="text-gray-600">
                    {formatTimestamp(charge.created)}
                  </p>
                </div>
              </div>

              <hr className="my-6 border-gray-200" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Status</h6>
                    <p>{charge.refunded ? "Refunded" : charge.status.charAt(0).toUpperCase() + charge.status.slice(1)}</p>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Description</h6>
                    <p>{charge.description || "No description"}</p>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Payment Method</h6>
                    <p>{charge.payment_method || "Unknown"}</p>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Customer</h6>
                    <p className="text-sm font-normal">{charge.billing_details.name || "No customer"}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-light">{charge.billing_details.email || "No email"}</p>
                      {charge.billing_details.email && (
                        <button
                          onClick={() => handleCopyEmail(charge.billing_details.email || "")}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Amount</h6>
                    <p>{formatCurrency(charge.amount, charge.currency)}</p>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Amount Captured</h6>
                    <p>{formatCurrency(charge.amount_captured, charge.currency)}</p>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Amount Refunded</h6>
                    <p>{formatCurrency(charge.amount_refunded, charge.currency)}</p>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-sm font-semibold text-gray-600">Balance Transaction</h6>
                    <p>{charge.balance_transaction || "None"}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-start gap-4">
                {charge.receipt_url && (
                  <div className="mt-4">
                    <a
                      href={charge.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      View Receipt
                    </a>
                  </div>
                )}
                {!charge.refunded && (
                  <div className="mt-4">
                    <StripeRefundButton
                      chargeId={charge.id}
                      maxAmount={charge.amount - charge.amount_refunded}
                      currency={charge.currency}
                      onRefundComplete={() => {
                        // Refresh the charge data after refund
                        if (id) {
                          fetchCharge();
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              {charge.failure_message && (
                <div className="mt-6">
                  <h6 className="text-sm font-semibold text-red-600">Failure Message</h6>
                  <p className="text-red-600">{charge.failure_message}</p>
                  {charge.failure_code && (
                    <p className="text-sm text-red-600">
                      Code: {charge.failure_code}
                    </p>
                  )}
                </div>
              )}

              {charge.refunded && (
                <div className="mt-6">
                  <h6 className="text-sm font-semibold text-gray-600">Refund Details</h6>
                  <p>This charge has been refunded.</p>
                  {charge?.refunds?.length > 0 && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Refund ID
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Amount
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {charge.refunds.map((refund: any) => (
                            <tr key={refund.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {refund.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(refund.amount, charge.currency)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatTimestamp(refund.created)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {charge.disputed && (
                <div className="mt-6">
                  <h6 className="text-sm font-semibold text-red-600">Dispute Information</h6>
                  <p>This charge has been disputed.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BasicLayout>
  );
};

export default ChargeDetail; 